import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { Globe } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_marketing-economy/artifacts/q6npai4a_907F1F75-F4C3-4C66-925B-49CBEC490807.PNG";

// Simple SVG icons for social media
const InstagramIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const Footer = () => {
  const { language, toggleLanguage, t } = useLanguage();
  
  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <footer className="bg-[#0F172A] border-t border-white/5" data-testid="footer">
      <div className="section-container py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <a href="/" className="flex items-center gap-2 mb-4" data-testid="footer-logo">
              <img src={LOGO_URL} alt="AQERA" className="h-10 w-auto" />
            </a>
            <p className="text-[#64748B] text-sm leading-relaxed mb-6">
              {t.footer.description}
            </p>
            
            {/* Social Links */}
            <div className="flex gap-4">
              <a
                href="https://instagram.com/aqera.app"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-[#94A3B8] hover:bg-[#2563EB] hover:text-white transition-all"
                data-testid="social-instagram"
              >
                <InstagramIcon />
              </a>
              <a
                href="https://facebook.com/aqera.app"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-[#94A3B8] hover:bg-[#2563EB] hover:text-white transition-all"
                data-testid="social-facebook"
              >
                <FacebookIcon />
              </a>
            </div>
          </div>
          
          {/* Navigation Column */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t.footer.navigation}</h4>
            <nav className="space-y-3">
              <button
                onClick={() => scrollToSection('#solution')}
                className="block text-[#94A3B8] hover:text-white transition-colors text-sm"
              >
                {t.nav.solution}
              </button>
              <button
                onClick={() => scrollToSection('#how-it-works')}
                className="block text-[#94A3B8] hover:text-white transition-colors text-sm"
              >
                {t.nav.howItWorks}
              </button>
              <button
                onClick={() => scrollToSection('#for-brands')}
                className="block text-[#94A3B8] hover:text-white transition-colors text-sm"
              >
                {t.nav.forBrands}
              </button>
              <button
                onClick={() => scrollToSection('#pricing')}
                className="block text-[#94A3B8] hover:text-white transition-colors text-sm"
              >
                {t.nav.pricing}
              </button>
              <button
                onClick={() => scrollToSection('#faq')}
                className="block text-[#94A3B8] hover:text-white transition-colors text-sm"
              >
                {t.nav.faq}
              </button>
            </nav>
          </div>
          
          {/* Legal Column */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t.footer.legal}</h4>
            <nav className="space-y-3">
              <a
                href="/privacy"
                className="block text-[#94A3B8] hover:text-white transition-colors text-sm"
              >
                {t.footer.privacy}
              </a>
              <a
                href="/terms"
                className="block text-[#94A3B8] hover:text-white transition-colors text-sm"
              >
                {t.footer.terms}
              </a>
              <a
                href="mailto:contact@aqera.app"
                className="block text-[#94A3B8] hover:text-white transition-colors text-sm"
              >
                {t.footer.contact}
              </a>
            </nav>
          </div>
          
          {/* Language & Dashboard Column */}
          <div>
            <h4 className="text-white font-semibold mb-4">{t.footer.social}</h4>
            
            {/* Language Toggle */}
            <button
              onClick={toggleLanguage}
              className="flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors text-sm mb-6"
              data-testid="footer-language-toggle"
            >
              <Globe className="w-4 h-4" />
              <span>{language === 'fr' ? 'English' : 'Français'}</span>
            </button>
            
            {/* Dashboard Link */}
            <a
              href="https://dashboard.aqera.app"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm py-3 px-5 inline-flex"
              data-testid="footer-dashboard"
            >
              Dashboard
            </a>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/5">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#64748B] text-sm">
              {t.footer.copyright}
            </p>
            <div className="flex items-center gap-6">
              <span className="text-[#64748B] text-xs">
                Montréal, Canada
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
